import { ManipulateType } from 'dayjs';
import { IntervalType, CampaignType } from '@dev/ee-api/ee-api-models';
import { parse } from 'query-string';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ContentLoader, DateDropdown, FilterIcon, FiltersPopover, palette, Typography } from '@dev/ee-components';
import styled from 'styled-components';
import { includes } from 'lodash-es';
import { PickedCampaignInterface } from '@/store/campaigns/types';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { GetCampaignReportsSummary } from '@/store/campaigns/actions';
import dayjs from '@/services/tools/dayjs';
import useMobile from '@/services/hooks/useMobile';
import { SHORT_DATE_FORMAT, TIME_RANGES, TIME_RANGE_WEEK, LONG_DATE_FORMAT } from '@/services/constants';
import { useDateDropdownOptions } from '@/components/Campaigns/hooks/useDateDropdownOptions';
import { getCampaignStartDate } from '../../helperFunctions';

export const GraphHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const FiltersContainer = styled.div`
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    margin-left: 8px;
    padding: 11px 11px;
`;

const getDateDropdownValue = (pickedCampaign: PickedCampaignInterface): number | string => {
    const query = parse(location.search, { arrayFormat: 'comma' });
    if (query.from) {
        const from = dayjs(query.from as string, SHORT_DATE_FORMAT);
        const to = dayjs(query.to as string, SHORT_DATE_FORMAT);
        const datesDiff = to.diff(from, 'day');

        if (includes(TIME_RANGES, datesDiff)) {
            return datesDiff;
        } else if (
            to.diff(dayjs(getCampaignStartDate(pickedCampaign.campaign, SHORT_DATE_FORMAT)), 'day') === datesDiff
        ) {
            return DateDropdownOptions.All;
        } else {
            return DateDropdownOptions.Calendar;
        }
    } else {
        return TIME_RANGE_WEEK;
    }
};

export const ChartTitle = (props: { pickedCampaign: PickedCampaignInterface }): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();
    const dateDropdownOptions = useDateDropdownOptions();
    const dropdownDefaultValue = getDateDropdownValue(props.pickedCampaign);
    const calendarChange = (from: string, to: string): void => {
        const fromFormated = dayjs(from).startOf('day').utc().format(LONG_DATE_FORMAT);
        const toFormated = dayjs(to).endOf('day').utc().format(LONG_DATE_FORMAT);

        dispatch(
            GetCampaignReportsSummary({
                campaign: props.pickedCampaign?.campaign,
                from: fromFormated,
                to: toFormated,
            })
        );
    };

    const onDateFilterClick = (event: React.MouseEvent<HTMLElement>, dateValue: string | number): void => {
        const to = dateValue === DateDropdownOptions.All ? null : dayjs().endOf('day').utc().format(LONG_DATE_FORMAT);
        const from =
            dateValue === DateDropdownOptions.All
                ? null
                : dayjs()
                      .subtract(dateValue as number, 'days' as ManipulateType)
                      .startOf('day')
                      .utc()
                      .format(LONG_DATE_FORMAT);

        dispatch(
            GetCampaignReportsSummary({
                campaign: props.pickedCampaign?.campaign,
                ...(from ? { from } : {}),
                ...(to ? { to } : {}),
                interval: IntervalType.Summary,
            })
        );
    };

    const mobileFilterClick = (filtersObj, momentFrom, momentTo): void => {
        const pickedRadioValue = filtersObj[0];

        if (pickedRadioValue === DateDropdownOptions.Calendar) {
            calendarChange(momentFrom, momentTo);
        } else {
            onDateFilterClick(null, pickedRadioValue);
        }
    };

    switch (props.pickedCampaign?.campaign?.Type) {
        case CampaignType.SendNow:
        case CampaignType.AbSplit:
            return <Typography variant="h3">{t('48hperformance')}</Typography>;
        case CampaignType.Automated:
            return (
                <GraphHeader>
                    <Typography variant="h3">{t('performance')}</Typography>
                    <div>
                        {isMobile ? (
                            <FiltersPopover
                                onApply={mobileFilterClick}
                                tabs={[
                                    {
                                        label: t('daterange'),
                                        defaultValue: dropdownDefaultValue.toString(),
                                        options: dateDropdownOptions,
                                        type: 'date' as const,
                                    },
                                ]}
                                title={t('filters')}
                                translations={{
                                    apply: t('apply'),
                                    close: t('close'),
                                }}
                            >
                                <FiltersContainer>
                                    <FilterIcon color={palette.gray[6]} width={20} height={20} />
                                </FiltersContainer>
                            </FiltersPopover>
                        ) : (
                            <DateDropdown
                                options={dateDropdownOptions}
                                defaultValue={dropdownDefaultValue}
                                type="input"
                                returnRawData
                                calendarOnChange={(from, to) => calendarChange(from, to)}
                                onItemClick={(event, date) => onDateFilterClick(event, date.value)}
                                buttonVariant="tertiary"
                            />
                        )}
                    </div>
                </GraphHeader>
            );
        default:
            return <ContentLoader mode="row" />;
    }
};
